import React from 'react'
import {
  ButtonVariant,
  EmptyText,
  MaxWidthConstraint,
  OvalButton,
  PhotoHero,
  WorkArea,
} from '@revelrygroup/components'
import classnames from 'classnames'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import Image from 'next/image'
import { useRouter } from 'next/router'

import { API } from '../../revelry_api'
import EventTrackingModel from '../../components/models/EventTrackingModel'
import formElementStyles from '../../forms/elements/styles.module.scss'
import { FuneralHome, ObituarySnippet } from '../../types'
import { FuneralHomeFooter } from '../../components/FuneralHomeFooter'
import { FuneralHomeModel } from '../../components/models/FuneralHomeModel'
import Layout from '../../components/Layout'
import Obituaries from '../../components/Obituaries'
import styles from '../../styles/Celebrations.module.scss'
import { routes } from '../../routes'
import UrlModel from '../../components/models/UrlModel'

const PAGE_LIMIT = 24

interface Props {
  funeralHome: FuneralHome | null
  serverObits: ObituarySnippet[]
}

interface QueryParams {
  page?: number
  search?: string
}

const queryObject = (params: QueryParams): Record<string, string> => {
  const { page, search } = params

  return {
    ...(search && { search }),
    ...(page > 1 && { page: page.toString() }),
  }
}

const getObituarySnippets = (page: number, search: string): Promise<ObituarySnippet[]> =>
  API.obituarySnippets.loadAll({
    limit: PAGE_LIMIT,
    page,
    search,
  })

const ObituariesPage: React.FC<Props> = ({ funeralHome, serverObits }) => {
  const [obituaries, setObituaries] = React.useState(serverObits)
  const [canLoadMore, setCanLoadMore] = React.useState(serverObits.length === PAGE_LIMIT)
  const [loading, setLoading] = React.useState(false)
  const router = useRouter()
  const page = parseInt((router.query.page as string) || '1')
  const search = (router.query.search as string) || ''
  const isInitialRender = React.useRef<boolean>(true)
  const [formSearch, setFormSearch] = React.useState(search)
  const setParams = (queryParams: QueryParams) => {
    const { pathname } = router
    const query = queryObject(queryParams)

    router.push({ pathname, query }, undefined, { shallow: true })
  }
  const loadObits = async (): Promise<void> => {
    try {
      setLoading(true)
      const apiObits = await getObituarySnippets(page, search)

      setCanLoadMore(apiObits.length === PAGE_LIMIT)

      if (page === 1) {
        setObituaries(apiObits)
      } else {
        setObituaries(obituaries.concat(apiObits))
      }
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
    } else {
      loadObits()
    }
  }, [page, search])
  const nextLink = UrlModel.fullURL(
    UrlModel.generatePath(
      router.pathname,
      queryObject({
        ...router.query,
        page: page + 1,
      }),
    ),
  )

  return (
    <>
      <Head>
        <title>
          Obituaries - {FuneralHomeModel.headTagLocation(funeralHome)} - {funeralHome?.name}
        </title>

        <meta
          name="description"
          content={`Latest obituaries for ${FuneralHomeModel.headTagLocation(
            funeralHome,
          )}, served by ${funeralHome?.name}`}
          key="description"
        />

        <link rel="canonical" href={UrlModel.fullURL(routes.obituaries)} />

        {canLoadMore && <link rel="next" href={nextLink} />}

        {page > 1 && (
          <link
            rel="previous"
            href={UrlModel.fullURL(
              UrlModel.generatePath(
                router.pathname,
                queryObject({
                  ...router.query,
                  page: page - 1,
                }),
              ),
            )}
          />
        )}
      </Head>

      <Layout>
        <PhotoHero>Obituaries</PhotoHero>

        <div className={styles.container}>
          <main>
            <WorkArea style={{ backgroundColor: 'white' }}>
              <form
                className={styles.form}
                onSubmit={(e) => {
                  e.preventDefault()
                  if (!loading) {
                    EventTrackingModel.trackSearchObituaries(formSearch)
                    setParams({ search: formSearch, page: 1 })
                  }
                }}
              >
                <div className={styles.inputWrapper}>
                  <input
                    className={classnames(formElementStyles.input, styles.input)}
                    disabled={loading}
                    name="search"
                    onChange={(e) => setFormSearch(e.currentTarget.value)}
                    placeholder="Search by name"
                    value={formSearch}
                  />

                  {search.length > 0 && (
                    <button
                      className={styles.clearBtn}
                      onClick={() => {
                        setFormSearch('')
                        setParams({ search: '', page: 1 })
                      }}
                      type="button"
                    >
                      <Image
                        src="/icon-clear--dark.png"
                        alt="clear search"
                        height={14}
                        width={14}
                      />
                    </button>
                  )}

                  <button className={styles.submitBtn} type="submit">
                    <Image height={18} width={18} alt="search" src="/icon-search.png" />
                  </button>
                </div>
              </form>

              {obituaries.length > 0 ? (
                <MaxWidthConstraint>
                  <Obituaries obituaries={obituaries} />
                </MaxWidthConstraint>
              ) : (
                <EmptyText
                  header={<>No Results</>}
                  body={
                    <>We did not find any obituaries that matched your search. Please try again.</>
                  }
                />
              )}

              {canLoadMore && (
                <div className={styles.showMoreWrapper}>
                  <OvalButton
                    as="anchor"
                    href={loading ? '' : nextLink}
                    onClick={(e) => {
                      e.preventDefault()
                      setParams({ page: page + 1, search })
                    }}
                    type="button"
                    variant={ButtonVariant.INVERSE_LIGHT}
                  >
                    More Obituaries
                  </OvalButton>
                </div>
              )}
            </WorkArea>
          </main>

          <FuneralHomeFooter />
        </div>
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ query }) => {
  try {
    const page = parseInt((query.page as string) || '1')
    const search = (query.search as string) || ''
    const [serverObits, funeralHome] = await Promise.all([
      getObituarySnippets(page, search),
      API.funeralHomes.load(),
    ])

    return { props: { funeralHome, serverObits } }
  } catch (e) {
    return { props: { funeralHome: null, serverObits: [] } }
  }
}

export default ObituariesPage
